import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useSearch } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/lib/auth";
import { useToast } from "@/hooks/use-toast";
import { Loader2 } from "lucide-react";

export default function Login() {
  const { t } = useTranslation();
  const [isRegister, setIsRegister] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const searchString = useSearch();
  const rawRedirect = new URLSearchParams(searchString).get("redirect");
  const redirectTo = rawRedirect && rawRedirect.startsWith("/") && !rawRedirect.startsWith("//") ? rawRedirect : null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isRegister) {
        await register(name, email, password);
        toast({ title: t("auth.accountCreated"), description: t("auth.welcomeMasterclass") });
      } else {
        await login(email, password);
        toast({ title: t("auth.welcomeBackToast") });
      }
      navigate(redirectTo || "/");
    } catch (err: any) {
      toast({
        title: t("auth.error"),
        description: err.message || t("auth.somethingWrong"),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative flex min-h-[calc(100vh-4rem)] items-center justify-center px-4">
      <div className="absolute inset-0 gradient-mesh" />
      <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at 50% 30%, rgba(59, 130, 246, 0.06) 0%, transparent 60%)" }} />
      <div className="relative w-full max-w-md glass-panel depth-shadow-lg p-8">
        <div className="mb-6 text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-primary/20 border border-primary/30" style={{ boxShadow: "0 0 30px rgba(59, 130, 246, 0.2)" }}>
            <span className="text-lg font-bold text-primary">M</span>
          </div>
          <h1 className="text-2xl font-bold glow-text-sm" data-testid="text-auth-title">
            {isRegister ? t("auth.createAccount") : t("auth.welcomeBack")}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {isRegister ? t("auth.joinCommunity") : t("auth.signInContinue")}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {isRegister && (
            <div className="space-y-2">
              <Label htmlFor="name">{t("auth.fullName")}</Label>
              <Input
                id="name"
                placeholder="John Doe"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="glass-input"
                data-testid="input-name"
              />
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="email">{t("auth.email")}</Label>
            <Input
              id="email"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="glass-input"
              data-testid="input-email"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">{t("auth.password")}</Label>
            <Input
              id="password"
              type="password"
              placeholder={t("auth.minChars")}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
              className="glass-input"
              data-testid="input-password"
            />
          </div>
          <Button
            type="submit"
            className="w-full btn-gradient-capsule text-sm py-2.5 btn-cta-glow"
            disabled={loading}
            data-testid="button-submit-auth"
          >
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isRegister ? t("auth.createAccount") : t("nav.signIn")}
          </Button>
        </form>

        <div className="glass-divider my-6" />

        <div className="text-center">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setIsRegister(!isRegister)}
            data-testid="button-toggle-auth"
          >
            {isRegister
              ? t("auth.alreadyAccount")
              : t("auth.noAccount")}
          </Button>
        </div>
      </div>
    </div>
  );
}
